import React, { useEffect, useRef } from "react";
import { pickColor, rgba, hexToRgb } from "./internal/colorUtils";

export const key = "double-pendulum";

const DEFAULT_BG = "#060914";
const DEFAULT_ARM = "#94a3b8";
const DEFAULT_TRAIL = "#22d3ee";
const DEFAULT_BOB = "#f472b6";

// Double-pendulum equations of motion derived from the Lagrangian, in
// the standard form (θ measured from the downward vertical):
//
//   θ1' = ω1
//   θ2' = ω2
//   ω1' = (−g(2m1+m2) sin θ1 − m2 g sin(θ1−2θ2)
//          − 2 sin(θ1−θ2) m2 (ω2² L2 + ω1² L1 cos(θ1−θ2)))
//          / (L1 (2m1 + m2 − m2 cos(2(θ1−θ2))))
//   ω2' = (2 sin(θ1−θ2)
//          (ω1² L1 (m1+m2) + g(m1+m2) cos θ1 + ω2² L2 m2 cos(θ1−θ2)))
//          / (L2 (2m1 + m2 − m2 cos(2(θ1−θ2))))
//
// CRITICAL: the previous version used the *render-side* arm fractions
// (~0.20) directly inside these equations, which made every
// acceleration term ~5× larger than the formula intended (the system
// has a 1/L factor everywhere). Here we keep simulation units and
// render units strictly separated:
//
//   • simulation: L1_SIM = L2_SIM = 1.0, G = 9.81  (standard physics
//     units, period ≈ 2 s for small oscillations);
//   • rendering:  L1_PX = minDim · L1_RENDER_FRAC, etc., applied only
//     when projecting state to canvas pixels.
//
// The pendulum is famously chaotic — the tip of the lower bob carves a
// beautifully tangled path. RK4 with a small dt keeps the trajectory
// stable for the lifetime of a tab without visible energy drift.
const M1 = 1.0;
const M2 = 1.0;
const L1_SIM = 1.0;
const L2_SIM = 1.0;
const G = 9.81;

// Total simulated time per frame = STEPS_PER_FRAME × DT seconds. With
// (4, 0.005) → 0.02 sim-s per frame ≈ 1.2 sim-s per real second at
// 60 Hz, which is just slightly under realtime — slow enough to follow
// the chaos by eye, fast enough that you don't wait around for it.
const DT = 0.005;
const STEPS_PER_FRAME = 4;

// Render-only constants (pure pixel scaling, never enter the dynamics).
const L1_RENDER_FRAC = 0.20;
const L2_RENDER_FRAC = 0.22;

const TRAIL_MAX = 1400;          // ~7.7 s of history at 180 samples/s
const TRAIL_MIN_SEG_PX2 = 0.25;  // skip render segments shorter than √0.25 px

function blendRgba(hexA, hexB, t, alpha) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return rgba(hexA, alpha);
  const clamp = Math.max(0, Math.min(1, t));
  const r = Math.round(a.r + (b.r - a.r) * clamp);
  const g = Math.round(a.g + (b.g - a.g) * clamp);
  const bl = Math.round(a.b + (b.b - a.b) * clamp);
  return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
}

// Right-hand side of the ODE — returns [dθ1, dθ2, dω1, dω2] at the
// given state. Closed-form derivatives keep RK4 cheap; we don't need
// numerical differentiation. Note we use SIM-side arm lengths
// exclusively here.
function deriv(t1, t2, w1, w2) {
  const delta = t1 - t2;
  const sinD = Math.sin(delta);
  const cosD = Math.cos(delta);
  const den = 2 * M1 + M2 - M2 * Math.cos(2 * delta);

  const dw1 =
    (-G * (2 * M1 + M2) * Math.sin(t1)
      - M2 * G * Math.sin(t1 - 2 * t2)
      - 2 * sinD * M2 * (w2 * w2 * L2_SIM + w1 * w1 * L1_SIM * cosD))
    / (L1_SIM * den);

  const dw2 =
    (2 * sinD
      * (w1 * w1 * L1_SIM * (M1 + M2)
         + G * (M1 + M2) * Math.cos(t1)
         + w2 * w2 * L2_SIM * M2 * cosD))
    / (L2_SIM * den);

  return [w1, w2, dw1, dw2];
}

function rk4Step(s, dt) {
  const k1 = deriv(s.t1, s.t2, s.w1, s.w2);
  const k2 = deriv(
    s.t1 + (dt / 2) * k1[0],
    s.t2 + (dt / 2) * k1[1],
    s.w1 + (dt / 2) * k1[2],
    s.w2 + (dt / 2) * k1[3],
  );
  const k3 = deriv(
    s.t1 + (dt / 2) * k2[0],
    s.t2 + (dt / 2) * k2[1],
    s.w1 + (dt / 2) * k2[2],
    s.w2 + (dt / 2) * k2[3],
  );
  const k4 = deriv(
    s.t1 + dt * k3[0],
    s.t2 + dt * k3[1],
    s.w1 + dt * k3[2],
    s.w2 + dt * k3[3],
  );
  s.t1 += (dt / 6) * (k1[0] + 2 * k2[0] + 2 * k3[0] + k4[0]);
  s.t2 += (dt / 6) * (k1[1] + 2 * k2[1] + 2 * k3[1] + k4[1]);
  s.w1 += (dt / 6) * (k1[2] + 2 * k2[2] + 2 * k3[2] + k4[2]);
  s.w2 += (dt / 6) * (k1[3] + 2 * k2[3] + 2 * k3[3] + k4[3]);
}

export default function DoublePendulum({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_ARM,
    DEFAULT_TRAIL,
    DEFAULT_BOB,
  ]);
  // Initial state: both arms raised near-inverted but pointing in
  // opposite horizontal directions, plus a non-zero initial spin on the
  // lower bob. This puts the system at very high potential energy in a
  // strongly asymmetric configuration — gravity rips both arms down
  // immediately and the lower bob is already moving, so we get violent
  // chaotic motion from frame 1 instead of a slow wind-up.
  //
  //   t1 ≈ 2.8 rad (~160°)  → upper arm up-and-to-the-right (almost inverted)
  //   t2 ≈ −2.5 rad (~−143°)  → lower arm up-and-to-the-left (almost inverted)
  //   w2 ≈ 4 rad/s            → lower bob given a CCW spin to break any
  //                              residual symmetry of the release
  const stateRef = useRef({
    t1: 2.8,
    t2: -2.5,
    w1: 0,
    w2: 4,
    // Trail samples store the four trig terms { s1, c1, s2, c2 }
    // separately so projection is exact for arbitrary L1/L2 render
    // fractions, and a canvas resize doesn't invalidate the buffer.
    trail: [],
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_ARM),
      pickColor(colors, 2, DEFAULT_TRAIL),
      pickColor(colors, 3, DEFAULT_BOB),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Trail is stored in sim space, so it survives resize — no clear.
    };

    const tick = () => {
      const [bg, armCol, trailCol, bobCol] = colorsRef.current;
      const state = stateRef.current;

      // Sub-step the simulation AND sample the trail per sub-step. The
      // per-step sample is what gives the tip trace its smooth, swept-
      // ribbon look — sampling once per frame leaves visible gaps when
      // the lower bob is whipping around at peak velocity.
      for (let i = 0; i < STEPS_PER_FRAME; i += 1) {
        rk4Step(state, DT);
        state.trail.push({
          s1: Math.sin(state.t1),
          c1: Math.cos(state.t1),
          s2: Math.sin(state.t2),
          c2: Math.cos(state.t2),
        });
      }
      if (state.trail.length > TRAIL_MAX) {
        state.trail.splice(0, state.trail.length - TRAIL_MAX);
      }

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Layout: pivot anchored above center so the pendulum has room
      // to swing fully below it. Arm pixel lengths = sim length × the
      // chosen render fraction × min canvas dim.
      const cx = width / 2;
      const cy = height * 0.42;
      const minDim = Math.min(width, height);
      const L1_PX = minDim * L1_RENDER_FRAC;
      const L2_PX = minDim * L2_RENDER_FRAC;
      // Joints in the live pendulum.
      const x1 = cx + L1_PX * Math.sin(state.t1);
      const y1 = cy + L1_PX * Math.cos(state.t1);
      const x2 = x1 + L2_PX * Math.sin(state.t2);
      const y2 = y1 + L2_PX * Math.cos(state.t2);

      // Draw the trail. Each segment's color/alpha is interpolated from
      // tail (oldest) → head (newest). Skipping near-zero-length
      // segments (the tip nearly stationary at swing extremes) avoids
      // wasting fillrate on invisible draws.
      const trail = state.trail;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const n = trail.length;
      let prevX = null;
      let prevY = null;
      for (let i = 0; i < n; i += 1) {
        const s = trail[i];
        const px = cx + L1_PX * s.s1 + L2_PX * s.s2;
        const py = cy + L1_PX * s.c1 + L2_PX * s.c2;
        if (prevX !== null) {
          const dx = px - prevX;
          const dy = py - prevY;
          if (dx * dx + dy * dy >= TRAIL_MIN_SEG_PX2) {
            const ageT = i / Math.max(1, n - 1); // 0 = oldest, 1 = newest
            const alpha = 0.04 + ageT * ageT * 0.85;
            ctx.strokeStyle = blendRgba(trailCol, bobCol, ageT, alpha);
            ctx.lineWidth = 0.7 + ageT * 1.4;
            ctx.beginPath();
            ctx.moveTo(prevX, prevY);
            ctx.lineTo(px, py);
            ctx.stroke();
          }
        }
        prevX = px;
        prevY = py;
      }

      // Pivot.
      ctx.fillStyle = rgba(armCol, 0.85);
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fill();

      // Upper arm.
      ctx.strokeStyle = rgba(armCol, 0.92);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(x1, y1);
      ctx.stroke();

      // Mid joint (bob 1).
      ctx.fillStyle = rgba(armCol, 0.95);
      ctx.beginPath();
      ctx.arc(x1, y1, 4.5, 0, Math.PI * 2);
      ctx.fill();

      // Lower arm.
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Lower bob with halo — it's the one whose tip we're tracing.
      const bobR = 5.5;
      const haloR = bobR * 4.5;
      const halo = ctx.createRadialGradient(x2, y2, 0, x2, y2, haloR);
      halo.addColorStop(0, rgba(bobCol, 0.6));
      halo.addColorStop(1, rgba(bobCol, 0));
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(x2, y2, haloR, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = rgba(bobCol, 1);
      ctx.beginPath();
      ctx.arc(x2, y2, bobR, 0, Math.PI * 2);
      ctx.fill();

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
